import get from 'lodash-es/get';
import map from 'lodash-es/map';
import filter from 'lodash-es/filter';
import reduce from 'lodash-es/reduce';
import find from 'lodash-es/find';
import flattenDeep from 'lodash-es/flattenDeep';
import toArray from 'lodash-es/toArray';
import forEach from 'lodash-es/forEach';
import { API } from '@/api/config';

/***
 * Класс работы с корзиной товаров
 */
class Basket {
  /***
   * Массив товаров в корзине
   * @type {Object[]|null}
   */
  static products = null;

  /***
   * Массив гифтсетов в корзине
   * @type {Object[]|null}
   */
  static giftset = null;

  /***
   * Ссылка на API
   * @type {string}
   */
  static baseUrl = !PROD ?
    `${API.DEVELOP_USER}/basket` :
    SAND ?
      `${API.SAND_USER}/basket`:
      `${API.PROD_USER}/basket`;

  /***
   * Добавление товаров в корзину
   * @param offerId{String} - id товара
   * @param quantity{Number} - количество товаров
   * @return {Promise<{quantity: Number, limit: Boolean, offerId: Number}>}
   */
  static updateProducts = ({ offerId, quantity }) => {
    const url = `${Basket.baseUrl}/change`;

    return fetch(url, {
      headers: {
        'Content-Type': 'application/json'
      },
      method: 'POST',
      body: JSON.stringify({ offerId, quantity }),
    })
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error(res.statusText);
      })
      .then((res) => {
        Basket.set(get(res, 'result.basket', null));
        const limit = get(res, 'result.basket.limit', false);
        const product = find(get(res, 'result.basket.products', []), (item) => item.offerId === offerId);
        return { limit, offerId: get(product, 'offerId', offerId), quantity: get(product, 'quantity', 0)};
      })
      .catch((err) => {
        console.error(err);
      });
  };

  /***
   * Добавление товаров в корзину
   * @param id{String} - id гифтсета
   * @param quantity{Number} - количество гифтсетоов
   * @return {Promise<{quantity: Number, limit: Boolean, offerId: Number}>}
   */
  static updateGiftset = ({ id, quantity }) => {
    const url = `${Basket.baseUrl}/changeGiftset`;
    return fetch(url, {
      headers: {
        'Content-Type': 'application/json'
      },
      method: 'POST',
      body: JSON.stringify({ id, quantity }),
    })
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error(res.statusText);
      })
      .then((res) => {
        Basket.set(get(res, 'result.basket', null));
        const limit = get(res, 'result.basket.limit', false);
        const giftset = find(get(res, 'result.basket.giftset', []), (item) => item.id === id);
        return { limit, offerId: get(giftset, 'id', id), quantity: get(giftset, 'quantity', 0)};
      })
      .catch((err) => {
        console.error(err);
      });
  };

  /***
   * Обновить данные корзины
   */
  static fetch = () => {
    const url = `${Basket.baseUrl}/get`;

    fetch(url)
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error(res.statusText);
      })
      .then((res) => {
        Basket.set(get(res, 'result.basket', null));
        window.Block.ProductControl?.updateControl();
        window.Block.GiftSetControl?.updateControl();
      })
      .catch((err) => {
        console.error(err);
      });
  };

  /***
   * Установить данные из корзины в переменные
   * @param basket{Object}
   */
  static set = (basket) => {
    const products = get(basket, 'products', []);
    const giftSet = get(basket, 'giftset', []);
    const result = flattenDeep([products, map(giftSet, ({ products }) => map(products, (item) => item))]);
    const omitGifts = filter(products, ({ gift }) => !gift);
    Basket.products = omitGifts;
    Basket.giftset = giftSet;
    Basket.updateCounter(result);
  };

  /***
   * Обновить счётчики товаров в корзине на странице
   * @param basket{Object}
   */
  static updateCounter = (basket) => {
    const basketCounters = toArray(document.querySelectorAll('[data-counter-basket]'));
    const count = reduce(basket, (acc, { quantity }) => acc + Number(quantity), 0);
    forEach(basketCounters, (counter) => {
      const max = counter.dataset['max'] || Infinity;
      counter.innerHTML = count > max ? `${max}+` : count;
      counter.classList.toggle('d-none', !count > 0);
    });
  };

  /**
   * Установка данных корзины
   * @param basket{Object} - объект корзины
   * @param update{Boolean} - нужно ли обновлять контролы
   */
  static setData = ({ basket, update = true }) => {
    Basket.set(basket);
    if (update) {
      window.Block.ProductControl?.updateControl();
      window.Block.GiftSetControl?.updateControl();
    }
  };
}

if (NOT_PROD) Basket.fetch();

window.Block.Basket = Basket;
